import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { CHICKFILA_LOCATIONS_LAYER_NAME } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/chickfilaLocationsLayer';
import config from 'constants/apiConfig';
import { internalLayersKeys } from 'hooks/internalLayer/queryKeys';
import type {
  InternalLayer,
  ListLayersResponse,
} from 'hooks/internalLayer/types';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';

type UseEnsureChickfilaLocationsLayerParams = {
  groupGuid: string | null | undefined;
};

/** Shared locks so React Strict Mode remounts do not double-delete. */
const cleanupLocks = new Map<string, Promise<void>>();

async function removeChickfilaLocationsLayersForGroup({
  groupGuid,
  queryClient,
}: {
  groupGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const listResp = await axios.get<ListLayersResponse<InternalLayer>>(
    config.apiGateway.internal_layers,
    {
      params: {
        'filter[groupGuid]': groupGuid,
        per_page: '200',
        include: 'incident',
      },
    }
  );

  const toDelete = (listResp.data.data ?? []).filter(
    (layer) => layer.name === CHICKFILA_LOCATIONS_LAYER_NAME
  );

  if (toDelete.length === 0) {
    return;
  }

  await Promise.all(
    toDelete.map((layer) =>
      axios.delete(`${config.apiGateway.internal_layers}/${layer.id}`)
    )
  );

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() }),
    queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() }),
  ]);
}

/**
 * Remove previously auto-seeded Chick-fil-A Locations internal layers for the
 * current group (hub and incident-scoped). Idempotent; no-op once gone.
 */
export function useEnsureChickfilaLocationsLayer({
  groupGuid,
}: UseEnsureChickfilaLocationsLayerParams) {
  const queryClient = useQueryClient();
  const completedGroupRef = useRef<string | null>(null);

  useEffect(() => {
    if (!groupGuid) {
      return;
    }

    if (completedGroupRef.current === groupGuid) {
      return;
    }

    let cancelled = false;
    let cleanupPromise = cleanupLocks.get(groupGuid);
    if (!cleanupPromise) {
      cleanupPromise = removeChickfilaLocationsLayersForGroup({
        groupGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[Chick-fil-A Locations] Failed to remove seeded internal layer',
            error
          );
          throw error;
        })
        .finally(() => {
          cleanupLocks.delete(groupGuid);
        });
      cleanupLocks.set(groupGuid, cleanupPromise);
    }

    void cleanupPromise
      .then(() => {
        if (!cancelled) {
          completedGroupRef.current = groupGuid;
        }
      })
      .catch(() => {
        // Error already logged; allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [groupGuid, queryClient]);
}
